// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { ReactComponent as WindIcon } from "../../assets/icons/wind-02.svg";
import { ReactComponent as CloudRaining } from "../../assets/icons/cloud-raining-05.svg";
import { ReactComponent as SunIcon } from "../../assets/icons/sun.svg";
import { ReactComponent as TemperatureIcon } from "../../assets/icons/thermometer-02.svg";
import { ReactComponent as HumidityIcon } from "../../assets/icons/droplets-01.svg";
import { getOHHFC, getTHEFW } from "../../api/awsLamba";

// HRRR - Dynamic model definitions based on API response structure
const hrrrModels = [
  {
    id: 'precip',
    name: 'Precipitation',
    icon: CloudRaining,
    description: 'Hourly and cumulative precipitation forecasts',
    variations: ['Hourly', 'Cumulative']
  },
  {
    id: 'wind',
    name: 'Wind',
    icon: WindIcon,
    description: 'Wind speed at different heights',
    variations: ['wind10', 'wind80']
  },
  {
    id: 'temp',
    name: 'Temperature',
    icon: TemperatureIcon,
    description: 'Temperature forecasts'
  },
  {
    id: 'humidity',
    name: 'Humidity',
    icon: HumidityIcon,
    description: 'Humidity forecasts'
  },
  {
    id: 'solar',
    name: 'Solar',
    icon: SunIcon,
    description: 'Solar radiation forecasts',
    variations: ['solarL', 'solarS']
  }
];

// GEFS - For 10-day forecast
const gefsModels = [
  {
    id: 'Precipitation',
    name: 'Precipitation',
    icon: CloudRaining,
    description: 'Precipitation forecasts'
  },
  {
    id: 'Wind',
    name: 'Wind',
    icon: WindIcon,
    description: 'Wind speed forecasts'
  },
  {
    id: 'Temperature',
    name: 'Temperature',
    icon: TemperatureIcon,
    description: 'Temperature forecasts'
  }
];

const GEFSHRRRModelDataSourceForm = ({ forecastType, onSubmit, selectedAsset }) => {
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [availableVariations, setAvailableVariations] = useState([]);
  
  // Get the appropriate model list based on forecast type
  const modelList = forecastType.value === '48hr1' ? hrrrModels : gefsModels;
  
  // Fetch data from the appropriate API based on forecast type
  useEffect(() => {
    console.log('Selected Asset:', selectedAsset);
    
    const fetchForecastData = async () => {
      try {
        setLoading(true);
        setForecastData(null);
        
        // Ensure we have an asset with coordinates
        if (!selectedAsset || !selectedAsset.lat || !selectedAsset.lng) {
          throw new Error('Asset is missing coordinates');
        }
        
        const lat = parseFloat(selectedAsset.lat);
        const lng = parseFloat(selectedAsset.lng);
        
        // Validate coordinates
        if (isNaN(lat) || isNaN(lng)) {
          throw new Error('Invalid coordinates');
        }
        
        let response;
        
        // Call the appropriate API based on forecast type
        if (forecastType.value === '48hr1') {
          // HRRR - Use getOHHFC
          console.log(`Calling getOHHFC(${lat}, ${lng}) for HRRR data...`);
          response = await getOHHFC(lat, lng);
          console.log('HRRR Data (from getOHHFC):', response);
          
          // Validate the response has the expected structure
          if (!response || typeof response !== 'object') {
            throw new Error('Invalid HRRR data response');
          }
        } 
        else if (forecastType.value === '240hr3') {
          // GEFS - Use getTHEFW
          console.log(`Calling getTHEFW(${lat}, ${lng}) for GEFS data...`);
          response = await getTHEFW(lat, lng);
          console.log('GEFS Data (from getTHEFW):', response);
          
          // Validate the response has the expected structure
          if (!response || typeof response !== 'object') {
            throw new Error('Invalid GEFS data response');
          }
        }
        else {
          throw new Error(`Unknown forecast type: ${forecastType.value}`);
        }
        
        // Store the forecast data for use in the component
        setForecastData(response);
      } 
      catch (err) {
        console.error(`Error fetching ${forecastType.label} data:`, err);
        setError(`Failed to load ${forecastType.label} data: ${err.message}`);
      } 
      finally {
        setLoading(false);
      }
    };
    
    if (selectedAsset) {
      fetchForecastData();
    }
  }, [forecastType, selectedAsset]);
  
  // When a model is selected, check for available variations
  useEffect(() => {
    if (selectedModel && forecastData) {
      // Check if the selected model exists in the data
      const modelData = forecastData[selectedModel.id];
      
      if (modelData) {
        // If the model has a variations property, use it to check what's available
        if (selectedModel.variations) {
          // Filter to only variations that exist in the data
          const availableVars = selectedModel.variations.filter(variation => {
            // For nested objects like wind.wind80
            if (typeof modelData === 'object' && variation in modelData) {
              return true;
            }
            // For objects with Hourly/Cumulative
            else if (variation === 'Hourly' || variation === 'Cumulative') {
              return variation in modelData;
            }
            return false;
          });
          
          setAvailableVariations(availableVars);
        } else {
          // If there are no predefined variations, set to empty
          setAvailableVariations([]);
        }
      } else {
        console.warn(`Selected model '${selectedModel.id}' not found in forecast data`);
        setAvailableVariations([]);
      }
    } else {
      setAvailableVariations([]);
    }
  }, [selectedModel, forecastData]);
  
  // State for the selected variation
  const [selectedVariation, setSelectedVariation] = useState(null);
  
  // Select a model
  const handleModelSelect = (model) => {
    setSelectedModel(model);
    setSelectedVariation(null); // Reset variation when model changes
  };
  
  // Select a variation
  const handleVariationSelect = (variation) => {
    setSelectedVariation(variation);
  };
  
  // Submit the selected model
  const handleSubmit = () => {
    if (selectedModel) {
      // Determine if we need to include a variation in the ID
      const variationSuffix = selectedVariation ? `_${selectedVariation}` : '';
      const variationLabel = selectedVariation ? ` (${selectedVariation})` : '';
      
      // Basic model information for continuing the flow
      const forecastModel = {
        id: `${forecastType.value}_${selectedModel.id}${variationSuffix}`,
        label: `${forecastType.label} - ${selectedModel.name}${variationLabel}`,
        modelType: 'CACHED_FORECAST',
        routeShort: forecastType.value === '240hr3' ? 'gefs_forecast' : 'hrrr_forecast',
        methodType: 'nearest',
        variableType: selectedModel.id,
        variationKey: selectedVariation || null,
        forecastData: forecastData // Pass the full forecast data for rendering
      };
      
      onSubmit({
        label: forecastModel.label,
        value: forecastModel.id,
        model: forecastModel,
        variableType: selectedModel.id,
        variationKey: selectedVariation || null,
        disabled: false
      });
    }
  };
  
  // Determine if continue button should be enabled
  const canContinue = () => {
    // If model has variations, require a variation to be selected
    if (selectedModel && availableVariations.length > 0) {
      return !!selectedVariation;
    }
    // Otherwise just require a model to be selected
    return !!selectedModel;
  };
  
  return (
    <div className="space-y-4">
      <div className="pb-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          {forecastType.label} Parameter
        </h3>
        <p className="text-sm text-gray-500">
          {forecastType.value === '48hr1' 
            ? 'Using getOHHFC() API - Check console for full response'
            : 'Using getTHEFW() API - Check console for full response'}
        </p>
      </div>
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm mb-4">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="flex flex-col items-center justify-center p-8 border border-gray-200 rounded-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
          <p className="text-gray-500">
            {forecastType.value === '48hr1' 
              ? 'Calling getOHHFC() and logging results to console...'
              : 'Calling getTHEFW() and logging results to console...'}
          </p>
        </div>
      ) : (
        <>
          {forecastData ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-blue-700">
              <p className="font-medium mb-1">API Call Complete</p>
              <p>
                {forecastType.value === '48hr1' 
                  ? 'Called getOHHFC() with the selected asset coordinates.'
                  : 'Called getTHEFW() with the selected asset coordinates.'}
              </p>
              <p className="mt-2">Check your browser console to see the full API response.</p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-700">
              <p className="font-medium">No forecast data available</p>
              <p>API response did not contain expected data structure.</p>
            </div>
          )}
          
          <div className="mt-5">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Select a Parameter</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {modelList.map((model) => {
                const Icon = model.icon;
                const isSelected = selectedModel?.id === model.id;
                
                return (
                  <div
                    key={model.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-blue-500 bg-blue-50' 
                        : forecastData && forecastData[model.id]
                          ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed'
                    }`}
                    onClick={() => forecastData && forecastData[model.id] && handleModelSelect(model)}
                  >
                    <div className="flex items-start">
                      <div className={`p-2 rounded-full mr-3 ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        {Icon && <Icon className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />}
                      </div>
                      <div>
                        <h4 className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>
                          {model.name}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {model.description}
                        </p>
                        {forecastData && !forecastData[model.id] && (
                          <p className="text-xs text-red-500 mt-1">
                            Not available in current forecast data
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Variation selection */}
          {selectedModel && availableVariations.length > 0 && (
            <div className="mt-5">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Select a Variation</h4>
              <div className="flex flex-wrap gap-2">
                {availableVariations.map((variation) => (
                  <button
                    key={variation}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      selectedVariation === variation
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={() => handleVariationSelect(variation)}
                  >
                    {variation}
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Continue button */}
      <div className="flex justify-end pt-4 mt-2 border-t border-gray-200">
        <button
          onClick={handleSubmit}
          disabled={!canContinue() || loading}
          className={`px-4 py-2 rounded-md font-medium ${
            canContinue() && !loading
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default GEFSHRRRModelDataSourceForm;